import { useEffect, useState, useRef } from "react";
import { Routes, Route, Link } from "react-router-dom";
import DashShell, { StatCard } from "@/components/DashShell";
import { AdminLangProvider } from "@/auth/AdminLangCtx";
import { api, fmtMoney, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CreditCard, Globe, Package, CheckCircle2, XCircle, Trash2, Plus, Landmark,
  Wallet as WalletIcon, MessageCircle, Send, Archive, RotateCcw, History, ShieldOff, Shield,
  TrendingUp, TrendingDown, ChevronDown, ChevronUp, ChevronLeft, Circle, Monitor, Paperclip, Key, Copy, Pencil,
  Gift, ArrowDownToLine, Check, Search, ExternalLink,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = (counts) => [
  { key: "overview", label: "Vue d'ensemble", to: "/super-admin", icon: LayoutDashboard },
  { key: "admins", label: "Admins clients", to: "/super-admin/admins", icon: Users, match: "/super-admin/admins" },
  { key: "subs", label: "Abonnements", to: "/super-admin/subscriptions", icon: CreditCard, badge: counts.pending_subs, match: "/super-admin/subscriptions" },
  { key: "plans", label: "Plans tarifaires", to: "/super-admin/plans", icon: Package, match: "/super-admin/plans" },
  { key: "domains", label: "Commandes domaines", to: "/super-admin/domains", icon: Globe, badge: counts.pending_domains, match: "/super-admin/domains" },
  { key: "deposits", label: "Historique dépôts", to: "/super-admin/deposits", icon: WalletIcon, match: "/super-admin/deposits" },
  { key: "chat", label: "Chat support", to: "/super-admin/chat", icon: MessageCircle, badge: counts.unread_chats, match: "/super-admin/chat" },
  { key: "visitors", label: "Visiteurs en ligne", to: "/super-admin/visitors", icon: Monitor, match: "/super-admin/visitors" },
  { key: "trusted", label: "Banques de confiance", to: "/super-admin/trusted-banks", icon: Landmark, match: "/super-admin/trusted-banks" },
  { key: "inactive", label: "Banques inactives", to: "/super-admin/inactive-banks", icon: ShieldOff, match: "/super-admin/inactive-banks" },
  { key: "active-banks", label: "Banques actives", to: "/super-admin/active-banks", icon: ExternalLink, match: "/super-admin/active-banks" },
  { key: "referral-withdrawals", label: "Retraits parrainage", to: "/super-admin/referral-withdrawals", icon: Gift, match: "/super-admin/referral-withdrawals" },
  { key: "settings", label: "Paramètres", to: "/super-admin/settings", icon: SettingsIcon, match: "/super-admin/settings" },
];

export default function SuperAdmin() {
  const [stats, setStats] = useState({});
  const reload = () => api.get("/stats/super-admin").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    const intvl = setInterval(reload, 15000);
    return () => clearInterval(intvl);
  }, []);
  const counts = {
    pending_subs: stats.pending_subs || 0,
    pending_domains: stats.pending_domains || 0,
    unread_chats: stats.unread_chats || 0,
  };

  return (
    <AdminLangProvider>
      <DashShell role="super_admin" title="" nav={NAV(counts)}>
        <Routes>
          <Route index element={<Overview stats={stats} reload={reload} />} />
          <Route path="admins" element={<Admins />} />
          <Route path="subscriptions" element={<Subscriptions reload={reload} />} />
          <Route path="plans" element={<Plans />} />
          <Route path="domains" element={<DomainOrders reload={reload} />} />
          <Route path="deposits" element={<DepositHistory />} />
          <Route path="chat" element={<ChatSupport />} />
          <Route path="visitors" element={<VisitorTracking />} />
          <Route path="trusted-banks" element={<TrustedBanks />} />
          <Route path="inactive-banks" element={<InactiveBanks />} />
          <Route path="active-banks" element={<ActiveBanks />} />
          <Route path="referral-withdrawals" element={<ReferralWithdrawals />} />
          <Route path="settings" element={<PlatformSettings />} />
        </Routes>
      </DashShell>
    </AdminLangProvider>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────
function Overview({ stats }) {
  return (
    <div data-testid="super-overview">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Super Admin</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">Vue d'ensemble</h1>
        <p className="text-zinc-500 mt-1">Pilote toute la plateforme.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testId="stat-revenue" label="Revenus (actifs)" value={fmtMoney(stats.revenue, stats.currency)} color="gold" />
        <StatCard testId="stat-admins" label="Admins clients" value={stats.total_admins ?? 0} />
        <StatCard testId="stat-banks" label="Banques créées" value={stats.total_banks ?? 0} />
        <StatCard testId="stat-active-banks" label="Banques actives" value={stats.active_banks ?? 0} color="gold" />
        <StatCard testId="stat-bank-users" label="Utilisateurs finaux" value={stats.total_bank_users ?? 0} />
        <StatCard testId="stat-active-subs" label="Abonnements actifs" value={stats.active_subs ?? 0} />
        <StatCard testId="stat-pending-subs" label="Abonnements en attente" value={stats.pending_subs ?? 0} sub="À valider" />
        <StatCard testId="stat-pending-domains" label="Domaines à activer" value={stats.pending_domains ?? 0} />
        <StatCard testId="stat-unread-chats" label="Chats non lus" value={stats.unread_chats ?? 0} color="gold" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/super-admin/subscriptions" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6">
          <div className="text-sm text-zinc-500 mb-1">Gestion</div>
          <div className="font-display text-xl font-bold">Abonnements</div>
          <p className="text-zinc-500 text-sm mt-2">{stats.pending_subs ?? 0} abonnement(s) en attente de validation.</p>
        </Link>
        <Link to="/super-admin/plans" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6">
          <div className="text-sm text-zinc-500 mb-1">Configuration</div>
          <div className="font-display text-xl font-bold">Plans tarifaires</div>
          <p className="text-zinc-500 text-sm mt-2">Modifie prix, limites et fonctionnalités.</p>
        </Link>
        <Link to="/super-admin/deposits" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6">
          <div className="text-sm text-zinc-500 mb-1">Finance</div>
          <div className="font-display text-xl font-bold">Historique des dépôts</div>
          <p className="text-zinc-500 text-sm mt-2">Tous les crédits et débits des comptes Admins.</p>
        </Link>
        <Link to="/super-admin/chat" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6">
          <div className="text-sm text-zinc-500 mb-1">Support</div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            Chat support
            {(stats.unread_chats ?? 0) > 0 && (
              <span className="text-sm font-bold bg-red-600 text-white rounded-full px-2 py-0.5">{stats.unread_chats}</span>
            )}
          </div>
          <p className="text-zinc-500 text-sm mt-2">Conversations avec les visiteurs et clients.</p>
        </Link>
        <Link to="/super-admin/visitors" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6">
          <div className="text-sm text-zinc-500 mb-1">Trafic</div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            <Monitor className="w-5 h-5 text-emerald-600" /> Visiteurs en ligne
          </div>
          <p className="text-zinc-500 text-sm mt-2">Vois en temps réel qui navigue sur ton site.</p>
        </Link>
      </div>
    </div>
  );
}

// ─── Admins ──────────────────────────────────────────────────────────────────
function Admins() {
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [balOpen, setBalOpen] = useState(false);
  const [balForm, setBalForm] = useState({ type: "credit", amount: "", label: "" });
  const [expandedAdmin, setExpandedAdmin] = useState(null);
  const [delAdminConfirm, setDelAdminConfirm] = useState(null);
  const [resetLinkData, setResetLinkData] = useState(null); // { reset_link, expires_at, admin_email, admin_name }
  const [resetLinkBusy, setResetLinkBusy] = useState(false);
  const [renameAdmin, setRenameAdmin] = useState(null); // { id, name }
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  // Search by email/name + filter by balance range. Sent to backend as
  // ?q=...&balance_min=...&balance_max=... (all regex-safe). Debounced 250 ms.
  const [search, setSearch] = useState("");
  const [balanceMin, setBalanceMin] = useState("");
  const [balanceMax, setBalanceMax] = useState("");
  const searchTimerRef = useRef(null);

  const load = (q = "", bmin = "", bmax = "") => {
    const params = {};
    if (q) params.q = q;
    if (bmin !== "" && !Number.isNaN(Number(bmin))) params.balance_min = Number(bmin);
    if (bmax !== "" && !Number.isNaN(Number(bmax))) params.balance_max = Number(bmax);
    return api.get("/admins", { params })
      .then((r) => setAdmins(r.data))
      .catch((e) => toast.error(formatError(e)));
  };
  useEffect(() => { load(); }, []);
  // Debounce so we don't hit the server on every keystroke
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => load(search, balanceMin, balanceMax), 250);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, balanceMin, balanceMax]);

  const block = async (a) => {
    try {
      await api.post(`/admins/${a.id}/${a.blocked ? "unblock" : "block"}`);
      toast.success(a.blocked ? "Admin débloqué" : "Admin bloqué");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const openHist = async (a) => {
    setSelected(a); setHistory(null); setHistOpen(true);
    try { const { data } = await api.get(`/admins/${a.id}/history`); setHistory(data); }
    catch (e) { toast.error(formatError(e)); setHistOpen(false); }
  };

  const openBal = (a) => { setSelected(a); setBalForm({ type: "credit", amount: "", label: "" }); setBalOpen(true); };

  const openRename = (a) => { setRenameAdmin(a); setRenameValue(a.name || ""); };
  const submitRename = async () => {
    const name = renameValue.trim();
    if (!name) { toast.error("Le nom ne peut pas être vide"); return; }
    setRenameBusy(true);
    try {
      await api.patch(`/super-admin/admins/${renameAdmin.id}/name`, { name });
      toast.success("Nom de l'admin mis à jour");
      setRenameAdmin(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setRenameBusy(false); }
  };

  const genResetLink = async (a) => {
    setResetLinkBusy(true);
    try {
      const { data } = await api.post(`/super-admin/admins/${a.id}/reset-link`);
      setResetLinkData(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setResetLinkBusy(false); }
  };

  const copyLink = async () => {
    if (!resetLinkData?.reset_link) return;
    try {
      await navigator.clipboard.writeText(resetLinkData.reset_link);
      toast.success("Lien copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier — sélectionnez le lien manuellement");
    }
  };

  const deleteAdmin = async (id) => {
    try {
      await api.delete(`/super-admin/admins/${id}`);
      toast.success("Admin supprimé définitivement");
      setDelAdminConfirm(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const submitBalance = async () => {
    if (!balForm.amount || isNaN(Number(balForm.amount))) return;
    try {
      const { data } = await api.post(`/admins/${selected.id}/balance`, {
        type: balForm.type, amount: Number(balForm.amount), label: balForm.label,
      });
      toast.success(`Solde mis à jour : ${fmtMoney(data.new_balance, "COINS")}`);
      setBalOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="super-admins">
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Admins clients</h1>
          {/* 🔍 Search admins by email or name (debounced 250 ms, regex-safe server-side) */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
            <Input
              type="search"
              placeholder="Rechercher par email ou nom…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="admins-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-zinc-400 hover:bg-zinc-100"
                aria-label="Effacer la recherche"
                data-testid="admins-search-clear"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* 💰 Balance range filter — applied server-side via ?balance_min / ?balance_max */}
        <div className="flex flex-wrap items-end gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Solde min (Coins)</Label>
            <Input
              type="number"
              min="0"
              step="1000"
              placeholder="ex. 1000000"
              value={balanceMin}
              onChange={(e) => setBalanceMin(e.target.value)}
              className="w-40"
              data-testid="admins-balance-min"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Solde max (Coins)</Label>
            <Input
              type="number"
              min="0"
              step="1000"
              placeholder="ex. 5000000"
              value={balanceMax}
              onChange={(e) => setBalanceMax(e.target.value)}
              className="w-40"
              data-testid="admins-balance-max"
            />
          </div>
          {/* Quick presets — handy to spot suspicious balances */}
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => { setBalanceMin("1000000"); setBalanceMax(""); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
              data-testid="admins-preset-suspicious"
            >
              ≥ 1 M (suspects)
            </button>
            <button
              type="button"
              onClick={() => { setBalanceMin("100000000"); setBalanceMax(""); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
              data-testid="admins-preset-pirates"
            >
              ≥ 100 M (pirates)
            </button>
            <button
              type="button"
              onClick={() => { setBalanceMin(""); setBalanceMax("0"); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-200"
              data-testid="admins-preset-zero"
            >
              Solde 0
            </button>
            {(balanceMin || balanceMax) && (
              <button
                type="button"
                onClick={() => { setBalanceMin(""); setBalanceMax(""); }}
                className="px-3 py-1.5 text-xs font-semibold rounded-full text-zinc-500 hover:text-zinc-800"
                data-testid="admins-balance-clear"
              >
                Effacer
              </button>
            )}
          </div>
        </div>
      </div>
      {(search || balanceMin || balanceMax) && (
        <div className="text-xs text-zinc-500 mb-3" data-testid="admins-search-count">
          {admins.length} résultat{admins.length === 1 ? "" : "s"}
          {search && <> pour « {search} »</>}
          {(balanceMin || balanceMax) && (
            <>
              {" "}— solde
              {balanceMin && <> ≥ {Number(balanceMin).toLocaleString("fr-FR")}</>}
              {balanceMin && balanceMax && " et"}
              {balanceMax && <> ≤ {Number(balanceMax).toLocaleString("fr-FR")}</>}
            </>
          )}
        </div>
      )}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Nom / Email</th>
                <th className="text-right p-4">Solde</th>
                <th className="text-right p-4">Banques</th>
                <th className="text-right p-4">Abonnements</th>
                <th className="text-right p-4">Créé le</th>
                <th className="text-right p-4">Statut</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-zinc-400 text-sm" data-testid="admins-empty-state">
                    {(search || balanceMin || balanceMax)
                      ? "Aucun admin ne correspond aux filtres."
                      : "Aucun admin enregistré."}
                  </td>
                </tr>
              )}
              {admins.map((a) => (
                <>
                  <tr key={a.id} className={`border-t border-zinc-100 ${a.blocked ? "bg-red-50" : ""}`} data-testid={`admin-row-${a.id}`}>
                    <td className="p-4">
                      <div className="font-semibold">{a.name}</div>
                      <div className="text-zinc-500 text-xs">{a.email}</div>
                    </td>
                    <td className="p-4 text-right font-mono-num font-bold">{fmtMoney(a.admin_balance ?? 0, "COINS")}</td>
                    <td className="p-4 text-right font-mono-num">{a.banks_count}</td>
                    <td className="p-4 text-right font-mono-num">{a.subs_count}</td>
                    <td className="p-4 text-right text-zinc-500 text-xs">{new Date(a.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-4 text-right">
                      {a.blocked
                        ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Bloqué</Badge>
                        : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Actif</Badge>
                      }
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openBal(a)} title="Ajuster le solde">
                          <WalletIcon className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => block(a)} title={a.blocked ? "Débloquer" : "Bloquer"}
                          className={a.blocked ? "text-emerald-600" : "text-red-600"}>
                          {a.blocked ? <Shield className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openRename(a)} title="Renommer l'admin"
                          className="text-zinc-700 hover:bg-zinc-50">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openHist(a)} title="Historique complet">
                          <History className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => genResetLink(a)}
                          disabled={resetLinkBusy}
                          title="Générer un lien de réinitialisation"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200">
                          <Key className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDelAdminConfirm(a.id)} title="Supprimer définitivement" className="text-red-500 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                </>
              ))}
              {!admins.length && <tr><td colSpan={7} className="p-8 text-center text-zinc-400">Aucun admin pour l'instant.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Balance adjustment dialog */}
      <Dialog open={balOpen} onOpenChange={setBalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ajuster le solde — {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-zinc-500">Solde actuel : <span className="font-bold text-zinc-900">{fmtMoney(selected?.admin_balance ?? 0, "COINS")}</span></div>
            <div>
              <Label>Type d'opération</Label>
              <div className="flex gap-2 mt-1">
                <button onClick={() => setBalForm({ ...balForm, type: "credit" })}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-semibold transition ${balForm.type === "credit" ? "bg-emerald-600 text-white border-emerald-600" : "border-zinc-200 text-zinc-600"}`}>
                  <TrendingUp className="w-4 h-4" /> Créditer
                </button>
                <button onClick={() => setBalForm({ ...balForm, type: "debit" })}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-semibold transition ${balForm.type === "debit" ? "bg-red-600 text-white border-red-600" : "border-zinc-200 text-zinc-600"}`}>
                  <TrendingDown className="w-4 h-4" /> Débiter
                </button>
              </div>
            </div>
            <div>
              <Label>Montant (Coins)</Label>
              <Input type="number" min={1} value={balForm.amount} onChange={(e) => setBalForm({ ...balForm, amount: e.target.value })} placeholder="5000" autoFocus />
            </div>
            <div>
              <Label>Motif (optionnel)</Label>
              <Input value={balForm.label} onChange={(e) => setBalForm({ ...balForm, label: e.target.value })} placeholder="Rechargement manuel" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalOpen(false)}>Annuler</Button>
            <Button onClick={submitBalance} className={balForm.type === "credit" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}
              disabled={!balForm.amount}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename admin dialog */}
      <Dialog open={!!renameAdmin} onOpenChange={(o) => { if (!o) setRenameAdmin(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" /> Renommer l'admin
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-zinc-500">{renameAdmin?.email}</div>
            <div>
              <Label>Nom affiché</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Nom de l'admin"
                maxLength={80}
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && !renameBusy && submitRename()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameAdmin(null)}>Annuler</Button>
            <Button onClick={submitRename} disabled={renameBusy || !renameValue.trim()}
              className="bg-blue-600 text-white hover:bg-blue-700">
              {renameBusy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset link dialog */}
      <Dialog open={!!resetLinkData} onOpenChange={(o) => { if (!o) setResetLinkData(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Lien de réinitialisation généré
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-zinc-600">
              Lien à transmettre à <strong>{resetLinkData?.admin_name}</strong> ({resetLinkData?.admin_email})
              pour réinitialiser son mot de passe.
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">Lien à envoyer</div>
              <div className="font-mono text-xs break-all text-zinc-900 select-all">{resetLinkData?.reset_link}</div>
            </div>
            <div className="text-xs text-zinc-500">
              Expire le <strong className="text-zinc-700">
                {resetLinkData?.expires_at ? new Date(resetLinkData.expires_at).toLocaleString("fr-FR") : ""}
              </strong> (valide 24 heures).
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              ⚠️ Transmettez ce lien à l'admin par un canal sécurisé (WhatsApp, Telegram, SMS).
              Quiconque possède ce lien pourra changer son mot de passe.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetLinkData(null)}>Fermer</Button>
            <Button onClick={copyLink} className="bg-blue-600 text-white hover:bg-blue-700">
              <Copy className="w-4 h-4 mr-1.5" /> Copier le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete admin confirm dialog */}
      <Dialog open={!!delAdminConfirm} onOpenChange={(o) => { if (!o) setDelAdminConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer l'admin</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer cet admin définitivement ? Ses abonnements, transactions et commissions seront également supprimés. Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAdminConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => deleteAdmin(delAdminConfirm)}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin history dialog */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Historique complet — {selected?.name}</DialogTitle></DialogHeader>
          {!history ? (
            <div className="py-8 text-center text-zinc-400">Chargement…</div>
          ) : (
            <div className="overflow-y-auto flex-1 space-y-6 pr-1">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-zinc-50 rounded-xl p-3 text-center">
                  <div className="text-xs text-zinc-500">Solde</div>
                  <div className="font-bold font-mono-num">{fmtMoney(history.admin?.admin_balance ?? 0, "COINS")}</div>
                </div>
                <div className="bg-zinc-50 rounded-xl p-3 text-center">
                  <div className="text-xs text-zinc-500">Abonnements</div>
                  <div className="font-bold">{history.subscriptions?.length ?? 0}</div>
                </div>
                <div className="bg-zinc-50 rounded-xl p-3 text-center">
                  <div className="text-xs text-zinc-500">Banques</div>
                  <div className="font-bold">{history.banks?.length ?? 0}</div>
                </div>
                <div className="bg-zinc-50 rounded-xl p-3 text-center">
                  <div className="text-xs text-zinc-500">Domaines</div>
                  <div className="font-bold">{history.domains?.length ?? 0}</div>
                </div>
              </div>

              {/* Subscriptions */}
              <HistSection title="Abonnements" items={history.subscriptions} renderItem={(s) => (
                <div key={s.id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-semibold">{s.plan_name}</span>
                    <Badge variant="outline" className={`ml-2 text-xs ${s.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-600"}`}>{s.status}</Badge>
                  </div>
                  <div className="text-right text-zinc-500 text-xs">{fmtMoney(s.plan_price, "COINS")} · {new Date(s.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
              )} />

              {/* Banks */}
              <HistSection title="Banques créées" items={history.banks} renderItem={(b) => (
                <div key={b.id} className="flex items-center justify-between text-sm">
                  <div className="font-semibold">{b.name}</div>
                  <div className="text-zinc-500 text-xs">/{b.slug} · {new Date(b.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
              )} />

              {/* Domains */}
              <HistSection title="Domaines achetés" items={history.domains} renderItem={(d) => (
                <div key={d.id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-mono-num font-semibold">{d.domain}</span>
                    <Badge variant="outline" className={`ml-2 text-xs ${d.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{d.status}</Badge>
                  </div>
                  <div className="text-zinc-500 text-xs">{fmtMoney(d.price, "COINS")} · {new Date(d.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
              )} />

              {/* Wallet transactions */}
              <HistSection title="Opérations portefeuille" items={history.wallet_transactions} renderItem={(tx) => (
                <div key={tx.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    {tx.type === "credit"
                      ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                      : <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                    <span>{tx.label || (tx.type === "credit" ? "Crédit" : "Débit")}</span>
                  </div>
                  <div className={`font-mono-num font-bold text-xs ${tx.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>
                    {tx.type === "credit" ? "+" : "-"}{fmtMoney(tx.amount, "COINS")}
                    <span className="text-zinc-400 ml-2">{new Date(tx.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                </div>
              )} />
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistOpen(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistSection({ title, items, renderItem }) {
  const [open, setOpen] = useState(true);
  if (!items || items.length === 0) return (
    <div><div className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">{title} (0)</div></div>
  );
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 hover:text-zinc-900">
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {title} ({items.length})
      </button>
      {open && <div className="space-y-2 bg-zinc-50 rounded-xl p-3">{items.map(renderItem)}</div>}
    </div>
  );
}

// ─── Subscriptions ───────────────────────────────────────────────────────────
function Subscriptions({ reload }) {
  const [subs, setSubs] = useState([]);
  const [delConfirm, setDelConfirm] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const load = () => api.get("/subscriptions").then((r) => setSubs(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const deleteSub = async (id) => {
    try { await api.delete(`/super-admin/subscriptions/${id}`); toast.success("Abonnement supprimé"); setDelConfirm(null); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const cleanupSubs = async () => {
    try {
      const { data } = await api.post("/super-admin/cleanup", { targets: ["inactive_subs", "pending_subs"] });
      const total = (data.inactive_subs || 0) + (data.pending_subs || 0);
      toast.success(`${total} abonnement(s) supprimé(s)`);
      setCleanupConfirm(false);
      load();
      reload?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  const activate = async (id, sub) => {
    try {
      const endpoint = sub.is_upgrade_of ? `/subscriptions/${id}/activate-upgrade` : `/subscriptions/${id}/activate`;
      await api.post(endpoint);
      toast.success(sub.is_upgrade_of ? "Upgrade activé" : "Abonnement activé"); load(); reload?.();
    } catch (e) { toast.error(formatError(e)); }
  };
  const suspend = async (id) => {
    try { await api.post(`/subscriptions/${id}/suspend`); toast.success("Abonnement suspendu"); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="super-subs">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-extrabold tracking-tighter">Abonnements</h1><p className="text-zinc-500 mt-1">Valide ou suspend les abonnements payés.</p></div>
        <Button variant="outline" size="sm" onClick={() => setCleanupConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50 shrink-0">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Tout supprimer (inactifs)
        </Button>
      </div>
      <div className="space-y-3">
        {subs.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`sub-row-${s.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-display text-lg font-bold">{s.plan_name}</span>
                <Badge variant="outline" className={
                  s.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  s.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-zinc-100 text-zinc-600"
                }>{s.status}</Badge>
              </div>
              <div className="text-sm text-zinc-500 mt-1">{s.admin_email}</div>
            </div>
            <div className="text-right">
              <div className="font-mono-num font-bold">{fmtMoney(s.plan_price, "COINS")}</div>
              <div className="text-xs text-zinc-400">{new Date(s.created_at).toLocaleString("fr-FR")}</div>
            </div>
            <div className="flex gap-2 items-center">
              {s.status !== "active" && (
                <Button size="sm" onClick={() => activate(s.id, s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`sub-activate-${s.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> {s.is_upgrade_of ? "Activer l'upgrade" : "Activer"}
                </Button>
              )}
              {s.status === "active" && (
                <Button size="sm" variant="outline" onClick={() => suspend(s.id)} data-testid={`sub-suspend-${s.id}`}>
                  <XCircle className="w-4 h-4 mr-1" /> Suspendre
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setDelConfirm(s.id)} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0" title="Supprimer">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {!subs.length && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucun abonnement.</div>}
      </div>
      <Dialog open={cleanupConfirm} onOpenChange={(o) => { if (!o) setCleanupConfirm(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nettoyer les abonnements</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer tous les abonnements inactifs (suspendus, annulés, en attente) ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupConfirm(false)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={cleanupSubs}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!delConfirm} onOpenChange={(o) => { if (!o) setDelConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer l'abonnement</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer cet abonnement définitivement ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => deleteSub(delConfirm)}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Plans ───────────────────────────────────────────────────────────────────
const EMPTY_PLAN = {
  name: "", price: 25000, currency: "Coins", max_users: 15, max_domains: 1,
  description: "", features: [], extra_user_price: 3000, is_active: true, is_featured: false,
  maintenance_monthly: 2500, maintenance_annual: 30000,
};

function Plans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [featuresText, setFeaturesText] = useState("");
  const [delConfirm, setDelConfirm] = useState(null);

  const load = () => api.get("/plans", { params: { only_active: false } }).then((r) => setPlans(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const startCreate = () => { setEditing("new"); setForm(EMPTY_PLAN); setFeaturesText(""); };
  const startEdit = (p) => { setEditing(p.id); setForm(p); setFeaturesText((p.features || []).join("\n")); };
  const cancel = () => setEditing(null);

  const save = async () => {
    const payload = { ...form, features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/plans", payload);
      else await api.put(`/plans/${editing}`, payload);
      toast.success("Plan enregistré"); setEditing(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/plans/${id}`); toast.success("Plan supprimé"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="super-plans">
      <div className="mb-6 flex justify-between items-end">
        <div><h1 className="font-display text-3xl font-extrabold tracking-tighter">Plans tarifaires</h1><p className="text-zinc-500 mt-1">Crée et ajuste les packs proposés à tes clients.</p></div>
        <Button onClick={startCreate} className="btn-pill bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="plan-new">
          <Plus className="w-4 h-4 mr-1" /> Nouveau plan
        </Button>
      </div>
      {editing && (
        <div className="bg-white border-2 border-zinc-900 rounded-xl p-6 mb-6 space-y-4" data-testid="plan-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Nom</Label><Input value={form.name} onChange={upd("name")} data-testid="plan-name" /></div>
            <div><Label>Devise</Label><Input value={form.currency} onChange={upd("currency")} data-testid="plan-currency" /></div>
            <div><Label>Prix d'accès (Coins)</Label><Input type="number" value={form.price} onChange={upd("price")} data-testid="plan-price" /></div>
            <div><Label>Frais utilisateur supp.</Label><Input type="number" value={form.extra_user_price} onChange={upd("extra_user_price")} data-testid="plan-extra-price" /></div>
            <div><Label>Maintenance mensuelle (Coins)</Label><Input type="number" value={form.maintenance_monthly || 2500} onChange={upd("maintenance_monthly")} data-testid="plan-maintenance-monthly" /></div>
            <div><Label>Maintenance annuelle (Coins)</Label><Input type="number" value={form.maintenance_annual || 30000} onChange={upd("maintenance_annual")} data-testid="plan-maintenance-annual" /></div>
            <div><Label>Max utilisateurs</Label><Input type="number" value={form.max_users} onChange={upd("max_users")} data-testid="plan-max-users" /></div>
            <div><Label>Max domaines</Label><Input type="number" value={form.max_domains} onChange={upd("max_domains")} data-testid="plan-max-domains" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={upd("description")} rows={2} data-testid="plan-desc" /></div>
          <div>
            <Label>Fonctionnalités (une par ligne)</Label>
            <Textarea value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} rows={5} data-testid="plan-features" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="plan-active" />
            <span className="text-sm">Plan actif (visible publiquement)</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="plan-featured" />
            <span className="text-sm">⭐ Plan mis en avant (carte dorée "Recommandé" sur la page d'accueil)</span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={cancel} data-testid="plan-cancel">Annuler</Button>
            <Button onClick={save} className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="plan-save">Enregistrer</Button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-col" data-testid={`plan-row-${p.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl font-bold flex items-center gap-2">
                {p.name}
                {p.is_featured && <span className="text-xs bg-[#D4AF37] text-black font-bold px-2 py-0.5 rounded-full">⭐ Recommandé</span>}
              </div>
              {!p.is_active && <Badge variant="outline">Inactif</Badge>}
            </div>
            <div className="font-mono-num text-3xl font-extrabold mb-2">{fmtMoney(p.price, p.currency)}</div>
            <div className="text-sm text-zinc-500 mb-3">{p.description}</div>
            <ul className="text-sm space-y-1 mb-4 flex-1">
              <li>• {p.max_users} utilisateurs max</li>
              <li>• {p.max_domains} domaine(s)</li>
              <li>• +{p.extra_user_price} {p.currency} / user supp.</li>
              <li>• Maintenance : {p.maintenance_monthly || 2500} Coins/mois</li>
              <li>• ou {p.maintenance_annual || 30000} Coins/an</li>
            </ul>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(p)} className="flex-1" data-testid={`plan-edit-${p.id}`}>Modifier</Button>
              <Button size="sm" variant="outline" onClick={() => setDelConfirm(p.id)} className="text-red-600 hover:bg-red-50" data-testid={`plan-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!delConfirm} onOpenChange={(o) => { if (!o) setDelConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer le plan</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer ce plan ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => { del(delConfirm); setDelConfirm(null); }}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Domain Orders ────────────────────────────────────────────────────────────
function DomainOrders({ reload }) {
  const [orders, setOrders] = useState([]);
  const [delConfirm, setDelConfirm] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const load = () => api.get("/domains/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/domains/orders/${id}/${action}`); toast.success("Mis à jour"); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteOrder = async (id) => {
    try { await api.delete(`/super-admin/domain-orders/${id}`); toast.success("Commande supprimée"); setDelConfirm(null); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const cleanupOrders = async () => {
    try {
      const { data } = await api.post("/super-admin/cleanup", { targets: ["inactive_domains", "abandoned_orders"] });
      const total = (data.inactive_domains || 0) + (data.abandoned_orders || 0);
      toast.success(`${total} commande(s) supprimée(s)`);
      setCleanupConfirm(false);
      load();
      reload?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="super-domains">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-extrabold tracking-tighter">Commandes de domaines</h1></div>
        <Button variant="outline" size="sm" onClick={() => setCleanupConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50 shrink-0">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Tout supprimer (inactifs)
        </Button>
      </div>
      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`order-row-${o.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-mono-num font-bold text-lg">{o.domain}</span>
                <Badge variant="outline" className={
                  o.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  o.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                  "bg-amber-50 text-amber-700 border-amber-200"
                }>{o.status}</Badge>
                <Badge variant="outline" className="text-xs">{o.type === "connect" ? "Connexion DNS" : "Achat domaine"}</Badge>
              </div>
              <div className="text-sm text-zinc-500 mt-1">{o.admin_email}</div>
            </div>
            <div className="text-right"><div className="font-mono-num font-bold">{fmtMoney(o.price, o.currency)}</div></div>
            <div className="flex gap-2 items-center">
              {o.status === "pending" && (
                <>
                  <Button size="sm" onClick={() => act(o.id, "activate")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`order-activate-${o.id}`}>Activer</Button>
                  <Button size="sm" variant="outline" onClick={() => act(o.id, "reject")} data-testid={`order-reject-${o.id}`}>Rejeter</Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => setDelConfirm(o.id)} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0" title="Supprimer">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {!orders.length && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucune commande.</div>}
      </div>
      <Dialog open={cleanupConfirm} onOpenChange={(o) => { if (!o) setCleanupConfirm(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nettoyer les commandes</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer toutes les commandes inactives (rejetées, échouées, abandonnées) ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupConfirm(false)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={cleanupOrders}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!delConfirm} onOpenChange={(o) => { if (!o) setDelConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer la commande</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer cette commande de domaine définitivement ?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => deleteOrder(delConfirm)}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Deposit History ──────────────────────────────────────────────────────────
function DepositHistory() {
  const [txs, setTxs] = useState([]);
  const [delAllConfirm, setDelAllConfirm] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/super-admin/wallet-transactions").then((r) => setTxs(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const deleteTx = async (id) => {
    try { await api.delete(`/super-admin/wallet-transactions/${id}`); toast.success("Transaction supprimée"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteAll = async () => {
    try { await api.delete("/super-admin/wallet-transactions"); toast.success("Historique effacé"); setDelAllConfirm(false); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const filtered = txs.filter((tx) =>
    !filter || tx.admin_email?.includes(filter) || tx.admin_name?.toLowerCase().includes(filter.toLowerCase()) || tx.label?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="super-deposits">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Historique des dépôts</h1>
          <p className="text-zinc-500 mt-1">Toutes les transactions des portefeuilles Admins.</p>
        </div>
        <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDelAllConfirm(true)}>
          <Trash2 className="w-4 h-4 mr-1" /> Tout supprimer
        </Button>
      </div>

      <div className="mb-4">
        <Input placeholder="Filtrer par admin, email, libellé…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Admin</th>
                <th className="text-left p-4">Libellé</th>
                <th className="text-center p-4">Type</th>
                <th className="text-right p-4">Montant</th>
                <th className="text-right p-4">Solde après</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tx) => (
                <tr key={tx.id} className="border-t border-zinc-100">
                  <td className="p-4 text-zinc-500 text-xs whitespace-nowrap">
                    {new Date(tx.created_at).toLocaleString("fr-FR")}
                  </td>
                  <td className="p-4">
                    <div className="font-semibold text-xs">{tx.admin_name}</div>
                    <div className="text-zinc-400 text-xs">{tx.admin_email}</div>
                  </td>
                  <td className="p-4 text-zinc-600">{tx.label || "—"}</td>
                  <td className="p-4 text-center">
                    <Badge variant="outline" className={tx.type === "credit" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>
                      {tx.type === "credit" ? "Crédit" : "Débit"}
                    </Badge>
                  </td>
                  <td className={`p-4 text-right font-mono-num font-bold ${tx.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>
                    {tx.type === "credit" ? "+" : "-"}{fmtMoney(tx.amount, "COINS")}
                  </td>
                  <td className="p-4 text-right font-mono-num text-zinc-500">{fmtMoney(tx.balance_after, "COINS")}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="ghost" onClick={() => deleteTx(tx.id)} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-400">Aucune transaction{filter ? " correspondant au filtre" : ""}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={delAllConfirm} onOpenChange={setDelAllConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer tout l'historique</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer définitivement toutes les transactions ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAllConfirm(false)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={deleteAll}>Tout supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Chat Support ─────────────────────────────────────────────────────────────
function ChatSupport() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("open");
  const [sending, setSending] = useState(false);
  const msgsEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgRef = useRef(null);
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const loadConvs = () =>
    api.get("/super-admin/conversations").then((r) => setConvs(r.data)).catch(() => {});

  const loadMessages = async (convId) => {
    const { data } = await api.get(`/chat/${convId}/messages`);
    setMessages(data);
    if (data.length) lastMsgRef.current = data[data.length - 1].created_at;
    // Mark as read
    await api.post(`/super-admin/conversations/${convId}/read`).catch(() => {});
    setConvs((prev) => prev.map((c) => c.id === convId ? { ...c, unread_super: 0 } : c));
  };

  useEffect(() => {
    loadConvs();
    const intvl = setInterval(loadConvs, 5000);
    return () => clearInterval(intvl);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.id);
    pollRef.current = setInterval(() => loadMessages(selected.id), 3000);
    return () => clearInterval(pollRef.current);
  }, [selected?.id]);

  useEffect(() => {
    msgsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/super-admin/conversations/${selected.id}/reply`, { content: reply });
      setReply("");
      await loadMessages(selected.id);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };

  const sendFile = async (file) => {
    if (!selected || !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/super-admin/conversations/${selected.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadMessages(selected.id);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); fileInputRef.current && (fileInputRef.current.value = ""); }
  };

  const renderAttachment = (att, fromSA) => {
    if (!att) return null;
    const isImage = att.mime?.startsWith("image/");
    const url = att.url?.startsWith("http") ? att.url : `${BACKEND_URL}${att.url}`;
    if (isImage) return <img src={url} alt={att.filename} className="max-w-[220px] rounded-lg mt-1 block" />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className={`flex items-center gap-1.5 mt-1 text-xs underline ${fromSA ? "text-zinc-300" : "text-blue-600"}`}>
        <Paperclip className="w-3 h-3" />{att.filename}
      </a>
    );
  };

  const archiveConv = async (id) => {
    try { await api.post(`/super-admin/conversations/${id}/archive`); toast.success("Archivé"); loadConvs(); if (selected?.id === id) setSelected(null); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteConv = async (id) => {
    try { await api.delete(`/super-admin/conversations/${id}`); toast.success("Conversation supprimée"); loadConvs(); if (selected?.id === id) setSelected(null); }
    catch (e) { toast.error(formatError(e)); }
  };

  const reopenConv = async (id) => {
    try { await api.post(`/super-admin/conversations/${id}/reopen`); toast.success("Conversation réouverte"); loadConvs(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const displayedConvs = convs.filter((c) => filter === "all" || c.status === filter);

  const fmtTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <div data-testid="super-chat" className="h-[calc(100dvh-10rem)] md:h-[calc(100vh-14rem)] flex flex-col md:flex-row md:gap-4">
      {/* Conversation list — hidden on mobile when a conversation is selected */}
      <div className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-80 md:shrink-0 flex-col bg-white border border-zinc-200 rounded-xl overflow-hidden min-h-0`}>
        <div className="p-3 border-b border-zinc-100">
          <div className="flex gap-1 text-xs font-semibold">
            {["open", "archived", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`flex-1 py-1.5 rounded-lg transition ${filter === f ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
                {f === "open" ? "Ouvertes" : f === "archived" ? "Archivées" : "Toutes"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-zinc-100">
          {displayedConvs.map((c) => (
            <button key={c.id} onClick={() => setSelected(c)}
              className={`w-full text-left p-3 hover:bg-zinc-50 transition ${selected?.id === c.id ? "bg-zinc-50 border-l-2 border-zinc-900" : ""}`}>
              <div className="flex items-center justify-between mb-0.5 gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <Circle className={`w-2 h-2 shrink-0 ${c.visitor_online ? "fill-emerald-500 text-emerald-500" : "fill-zinc-300 text-zinc-300"}`} />
                  <span className="font-semibold text-sm truncate">{c.visitor_name || c.visitor_email || "Visiteur"}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {c.unread_super > 0 && (
                    <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{c.unread_super}</span>
                  )}
                  <span className="text-[10px] text-zinc-400">{fmtTime(c.last_message_at)}</span>
                </div>
              </div>
              {c.visitor_email && <div className="text-xs text-zinc-400 truncate">{c.visitor_email}</div>}
              {c.last_message && <div className="text-xs text-zinc-500 truncate mt-0.5">{c.last_message}</div>}
            </button>
          ))}
          {!displayedConvs.length && <div className="p-6 text-center text-zinc-400 text-sm">Aucune conversation.</div>}
        </div>
      </div>

      {/* Chat panel */}
      {selected ? (
        <div className="flex-1 flex flex-col bg-white border border-zinc-200 rounded-xl overflow-hidden min-w-0 min-h-0">
          {/* Header */}
          <div className="p-3 md:p-4 border-b border-zinc-100 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Back button — mobile only */}
              <button onClick={() => setSelected(null)}
                className="md:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-zinc-100 shrink-0"
                aria-label="Retour à la liste">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-semibold flex items-center gap-2 text-sm md:text-base">
                  <Circle className={`w-2.5 h-2.5 shrink-0 ${selected.visitor_online ? "fill-emerald-500 text-emerald-500" : "fill-zinc-300 text-zinc-300"}`} />
                  <span className="truncate">{selected.visitor_name || selected.visitor_email || "Visiteur anonyme"}</span>
                </div>
                {selected.visitor_email && <div className="text-xs text-zinc-400 truncate">{selected.visitor_email}</div>}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {selected.status === "archived" && (
                <Button size="sm" variant="outline" onClick={() => reopenConv(selected.id)} className="px-2 md:px-3">
                  <RotateCcw className="w-3.5 h-3.5 md:mr-1" /> <span className="hidden md:inline">Réouvrir</span>
                </Button>
              )}
              {selected.status === "open" && (
                <Button size="sm" variant="outline" onClick={() => archiveConv(selected.id)} className="px-2 md:px-3">
                  <Archive className="w-3.5 h-3.5 md:mr-1" /> <span className="hidden md:inline">Archiver</span>
                </Button>
              )}
              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 px-2" onClick={() => deleteConv(selected.id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "super_admin" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === "super_admin" ? "bg-zinc-900 text-white rounded-tr-sm" : "bg-zinc-100 text-zinc-900 rounded-tl-sm"}`}>
                  {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                  {m.attachment && renderAttachment(m.attachment, m.sender === "super_admin")}
                  <div className={`text-[10px] mt-1 ${m.sender === "super_admin" ? "text-zinc-400" : "text-zinc-500"}`}>
                    {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
            {!messages.length && <div className="text-center text-zinc-400 text-sm mt-8">Aucun message. Répondez pour démarrer.</div>}
            <div ref={msgsEndRef} />
          </div>

          {/* Reply input */}
          <div className="p-2 md:p-3 border-t border-zinc-100" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
            <div className="flex gap-1.5 md:gap-2 items-end">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Écrire une réponse…"
                rows={1}
                className="flex-1 resize-none min-h-[42px] max-h-32 text-sm"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                disabled={selected.status === "archived"}
              />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                disabled={sending || selected.status === "archived"} className="px-2.5 h-[42px] shrink-0" title="Joindre un fichier">
                <Paperclip className="w-4 h-4" />
              </Button>
              <Button onClick={sendReply} disabled={!reply.trim() || sending || selected.status === "archived"}
                className="bg-zinc-900 text-white px-3 h-[42px] shrink-0" size="sm">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <input ref={fileInputRef} type="file" className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp3,.wav,.ogg,.txt"
              onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])} />
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-white border border-zinc-200 rounded-xl text-zinc-400">
          <div className="text-center">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Sélectionnez une conversation pour commencer</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Visitor Tracking ─────────────────────────────────────────────────────────
function VisitorTracking() {
  const [visitors, setVisitors] = useState([]);
  const [initiateFor, setInitiateFor] = useState(null);
  const [initMsg, setInitMsg] = useState("");
  const [initSending, setInitSending] = useState(false);
  const pollRef = useRef(null);

  const load = () => api.get("/super-admin/visitors").then((r) => setVisitors(r.data)).catch(() => {});

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  const fmtTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const sec = Math.floor((now - d) / 1000);
    if (sec < 60) return `il y a ${sec}s`;
    if (sec < 3600) return `il y a ${Math.floor(sec / 60)}min`;
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const fmtDuration = (sec) => {
    if (!sec || sec < 0) return "0s";
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}min ${sec % 60}s`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}min`;
  };

  const fmtArrival = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const sendInitiate = async () => {
    if (!initMsg.trim() || !initiateFor) return;
    setInitSending(true);
    try {
      await api.post(`/super-admin/visitors/${initiateFor.session_id}/initiate`, { content: initMsg });
      toast.success("Message envoyé au visiteur !");
      setInitiateFor(null);
      setInitMsg("");
    } catch (err) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setInitSending(false);
    }
  };

  const online = visitors.filter((v) => v.visitor_online).length;

  return (
    <div data-testid="super-visitors">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Monitor className="w-7 h-7 text-emerald-600" /> Visiteurs en ligne
          </h1>
          <p className="text-zinc-500 mt-1">Suivi en temps réel des visiteurs actifs (5 dernières minutes).</p>
        </div>
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
          <Circle className="w-2.5 h-2.5 fill-emerald-500 text-emerald-500" />
          <span className="font-bold text-emerald-700">{online}</span>
          <span className="text-sm text-emerald-600">en ligne maintenant</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">IP</th>
                <th className="text-left p-4">Pays / Ville</th>
                <th className="text-left p-4">Page visitée</th>
                <th className="text-left p-4">Arrivée</th>
                <th className="text-left p-4">Temps sur site</th>
                <th className="text-center p-4">Statut</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map((v, i) => (
                <tr key={v.session_id || i} className="border-t border-zinc-100 hover:bg-zinc-50 transition">
                  <td className="p-4 font-mono text-xs text-zinc-600">{v.ip || "—"}</td>
                  <td className="p-4">
                    <div className="font-semibold text-zinc-800">{v.country || "—"}</div>
                    {v.city && <div className="text-xs text-zinc-400">{v.city}</div>}
                  </td>
                  <td className="p-4 font-mono text-xs text-zinc-500 max-w-[180px] truncate" title={v.page}>{v.page || "/"}</td>
                  <td className="p-4 text-xs text-zinc-500">{fmtArrival(v.first_seen)}</td>
                  <td className="p-4 text-xs text-zinc-600 font-mono">{fmtDuration(v.time_on_site)}</td>
                  <td className="p-4 text-center">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold ${v.visitor_online ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                      <Circle className={`w-1.5 h-1.5 ${v.visitor_online ? "fill-emerald-500" : "fill-zinc-400"}`} />
                      {v.visitor_online ? "En ligne" : "Hors ligne"}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => { setInitiateFor(v); setInitMsg(""); }}
                      className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-lg hover:bg-zinc-700 transition font-semibold"
                    >
                      Envoyer un message
                    </button>
                  </td>
                </tr>
              ))}
              {!visitors.length && (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-400">
                  <Monitor className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Aucun visiteur actif pour le moment.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Initiate dialog */}
      {initiateFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setInitiateFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-extrabold tracking-tighter mb-1">Envoyer un message</h3>
            <p className="text-sm text-zinc-500 mb-4">
              Visiteur sur <span className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-xs">{initiateFor.page || "/"}</span>
              {initiateFor.country && <> · {initiateFor.country}{initiateFor.city ? `, ${initiateFor.city}` : ""}</>}
            </p>
            <textarea
              value={initMsg}
              onChange={(e) => setInitMsg(e.target.value)}
              placeholder="Bonjour ! Puis-je vous aider ?"
              rows={3}
              className="w-full border border-zinc-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-zinc-900 resize-none mb-4"
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendInitiate()}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setInitiateFor(null)}
                className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition">
                Annuler
              </button>
              <button onClick={sendInitiate} disabled={!initMsg.trim() || initSending}
                className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-xl font-semibold hover:bg-zinc-700 disabled:opacity-40 transition">
                {initSending ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Trusted Banks (Confiance) ────────────────────────────────────────────────
function TrustedBanks() {
  const [banks, setBanks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", domain: "", country: "", logo_url: "", order: 0, is_active: true });
  const [delConfirm, setDelConfirm] = useState(null);

  const load = () => api.get("/super-admin/trusted-banks").then((r) => setBanks(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ name: "", domain: "", country: "", logo_url: "", order: banks.length, is_active: true }); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, domain: b.domain, country: b.country || "", logo_url: b.logo_url || "", order: b.order || 0, is_active: !!b.is_active }); };

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/super-admin/trusted-banks", payload);
        toast.success("Banque ajoutée");
      } else {
        await api.patch(`/super-admin/trusted-banks/${editing.id}`, payload);
        toast.success("Banque mise à jour");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/super-admin/trusted-banks/${id}`); toast.success("Banque supprimée"); setDelConfirm(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const logoFor = (b) => b.logo_url || (b.domain ? `https://logo.clearbit.com/${b.domain}` : "");

  return (
    <div data-testid="super-trusted">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Landmark className="w-7 h-7 text-zinc-700" /> Banques de confiance
          </h1>
          <p className="text-zinc-500 mt-1">Affichées dans le carrousel "Ils nous font confiance" sur la page publique de chaque banque.</p>
        </div>
        <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-700 text-white">
          <Plus className="w-4 h-4 mr-1" /> Ajouter une banque
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3 w-16">Logo</th>
                <th className="text-left p-3">Nom</th>
                <th className="text-left p-3">Domaine</th>
                <th className="text-left p-3">Pays</th>
                <th className="text-center p-3 w-20">Ordre</th>
                <th className="text-center p-3 w-24">Statut</th>
                <th className="text-right p-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {banks.map((b) => (
                <tr key={b.id} className="border-t border-zinc-100">
                  <td className="p-3">
                    <div className="w-10 h-10 rounded-md bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden">
                      {logoFor(b) ? <img src={logoFor(b)} alt={b.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                    </div>
                  </td>
                  <td className="p-3 font-semibold">{b.name}</td>
                  <td className="p-3 text-zinc-500 text-xs font-mono">{b.domain}</td>
                  <td className="p-3 text-zinc-600">{b.country || "—"}</td>
                  <td className="p-3 text-center font-mono-num text-zinc-500">{b.order ?? 0}</td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className={b.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500"}>
                      {b.is_active ? "Actif" : "Masqué"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openEdit(b)}>Modifier</Button>
                      <Button size="sm" variant="ghost" onClick={() => setDelConfirm(b)} className="text-red-500 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!banks.length && <tr><td colSpan={7} className="p-10 text-center text-zinc-400">Aucune banque enregistrée.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing === "new" ? "Ajouter une banque" : "Modifier la banque"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="BNP Paribas" />
            </div>
            <div>
              <Label>Domaine *</Label>
              <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value.trim().toLowerCase() })} placeholder="bnpparibas.com" />
              <p className="text-xs text-zinc-500 mt-1">Utilisé pour récupérer le logo via Clearbit.</p>
            </div>
            <div>
              <Label>Pays</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="France" />
            </div>
            <div>
              <Label>URL logo personnalisée (optionnel)</Label>
              <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
              <p className="text-xs text-zinc-500 mt-1">Si renseigné, remplace le logo Clearbit.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4" />
                  <span className="text-sm">Actif (visible)</span>
                </label>
              </div>
            </div>
            {form.domain && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-white border border-zinc-200 rounded-md flex items-center justify-center overflow-hidden">
                  <img src={form.logo_url || `https://logo.clearbit.com/${form.domain}`} alt="Aperçu" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="text-xs text-zinc-500">Aperçu du logo</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-700 text-white" onClick={save} disabled={!form.name.trim() || !form.domain.trim()}>
              {editing === "new" ? "Ajouter" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delConfirm} onOpenChange={(o) => { if (!o) setDelConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer cette banque</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer <strong>{delConfirm?.name}</strong> de la liste ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => remove(delConfirm.id)}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Inactive Banks ───────────────────────────────────────────────────────────
function InactiveBanks() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delConfirm, setDelConfirm] = useState(null);

  const REASON_LABELS = {
    subscription_inactive: "Abonnement inactif",
    maintenance_overdue: "Maintenance impayée",
    no_recent_login: "Aucune connexion (30j)",
  };

  const load = () => {
    setLoading(true);
    api.get("/super-admin/inactive-banks")
      .then((r) => setBanks(r.data || []))
      .catch(() => toast.error("Impossible de charger les banques inactives"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const del = async (bank) => {
    try {
      await api.delete(`/super-admin/inactive-banks/${bank.id}`);
      toast.success(`Banque "${bank.name}" supprimée`);
      setDelConfirm(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-2">Banques inactives</h1>
        <p className="text-zinc-500">Banques dont l'abonnement est suspendu, la maintenance impayée, ou sans connexion depuis 30 jours.</p>
      </div>

      {loading ? (
        <p className="text-zinc-400">Chargement…</p>
      ) : banks.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-green-800 font-medium">
          ✓ Aucune banque inactive pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {banks.map((b) => (
            <div key={b.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-zinc-900">{b.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{b.slug} · Abonnement : <span className={b.subscription_status === "active" ? "text-green-600" : "text-red-600"}>{b.subscription_status}</span></div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {b.reasons.map((r) => (
                    <span key={r} className="text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5">{REASON_LABELS[r] || r}</span>
                  ))}
                </div>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setDelConfirm(b)} className="shrink-0">
                <Trash2 className="w-4 h-4 mr-1" /> Supprimer
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!delConfirm} onOpenChange={(v) => !v && setDelConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer la banque</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            Supprimer définitivement <strong>{delConfirm?.name}</strong> et toutes ses données ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => del(delConfirm)}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Referral Withdrawals (Super Admin) ───────────────────────────────────────
const WMETHOD_LABEL = { mobile_money: "Mobile Money", wave: "Wave", crypto_usdt: "USDT Crypto", transfer_to_balance: "Transfert solde" };
const WSTATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  transferred: "bg-purple-100 text-purple-700",
};
const WSTATUS_LABEL = { pending: "En attente", approved: "Payé", rejected: "Rejeté", transferred: "Transféré" };

function ReferralWithdrawals() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [delAllConfirm, setDelAllConfirm] = useState(false);

  const load = (st = filter) => {
    setLoading(true);
    api.get(`/super-admin/referral-withdrawals${st !== "all" ? `?status=${st}` : ""}`)
      .then((r) => setList(r.data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/super-admin/referral-withdrawals/${id}/${action}`);
      toast.success(action === "approve" ? "Retrait approuvé !" : "Retrait rejeté");
      load(filter);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(null); }
  };

  const deleteAll = async () => {
    try {
      await api.delete("/super-admin/referral-withdrawals");
      toast.success("Historique supprimé");
      setDelAllConfirm(false);
      load(filter);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-1 flex items-center gap-2">
            <Gift className="w-6 h-6 text-[#D4AF37]" /> Retraits de parrainage
          </h1>
          <p className="text-zinc-500">Demandes de retrait des gains de parrainage en attente de paiement.</p>
        </div>
        <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDelAllConfirm(true)}>
          <Trash2 className="w-4 h-4 mr-1" /> Tout supprimer
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-sm px-4 py-1.5 rounded-full border font-medium transition ${filter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
            {s === "all" ? "Tous" : WSTATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-zinc-400">Chargement…</p>
      ) : list.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-8 text-center text-zinc-400 text-sm">
          Aucune demande de retrait{filter !== "all" ? ` (${WSTATUS_LABEL[filter]?.toLowerCase()})` : ""}.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((w) => (
            <div key={w.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-zinc-900">{w.admin_name}</span>
                  <span className="text-xs text-zinc-400">{w.admin_email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${WSTATUS_STYLE[w.status] || "bg-zinc-100 text-zinc-600"}`}>
                    {WSTATUS_LABEL[w.status] || w.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="font-bold text-zinc-900 font-mono">{new Intl.NumberFormat("fr-FR").format(w.amount)} Coins</span>
                  <span className="text-zinc-500">{WMETHOD_LABEL[w.method] || w.method}</span>
                  {w.method === "crypto_usdt"
                    ? <span className="font-mono text-xs text-zinc-400">{w.network?.toUpperCase()} · {w.address}</span>
                    : w.phone && <span className="text-zinc-500">{w.phone}</span>}
                </div>
                <div className="text-xs text-zinc-400 mt-1">{new Date(w.created_at).toLocaleString("fr-FR")}</div>
              </div>
              {w.status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                    disabled={busy === w.id + "approve"} onClick={() => act(w.id, "approve")}>
                    <Check className="w-3.5 h-3.5" /> Approuver
                  </Button>
                  <Button size="sm" variant="destructive" disabled={busy === w.id + "reject"} onClick={() => act(w.id, "reject")}>
                    Rejeter
                  </Button>
                </div>
              )}
              {w.status !== "pending" && w.resolved_at && (
                <div className="text-xs text-zinc-400 shrink-0">{new Date(w.resolved_at).toLocaleDateString("fr-FR")}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={delAllConfirm} onOpenChange={setDelAllConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer tout l'historique</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">Supprimer définitivement toutes les demandes de retrait ? Cette action est irréversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAllConfirm(false)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={deleteAll}>Tout supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Active Banks ──────────────────────────────────────────────────────────────
function ActiveBanks() {
  const [banks, setBanks] = useState([]);
  const [filter, setFilter] = useState("");
  const [delConfirm, setDelConfirm] = useState(null); // bank object pending deletion
  const [delBusy, setDelBusy] = useState(false);

  const load = () => api.get("/super-admin/active-banks").then((r) => setBanks(r.data || [])).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => { load(); }, []);

  const filtered = banks.filter((b) =>
    !filter ||
    b.name?.toLowerCase().includes(filter.toLowerCase()) ||
    b.slug?.includes(filter.toLowerCase()) ||
    b.admin_email?.includes(filter.toLowerCase())
  );

  const handleDelete = async () => {
    if (!delConfirm) return;
    setDelBusy(true);
    try {
      await api.delete(`/super-admin/active-banks/${delConfirm.id}`);
      toast.success(`Banque "${delConfirm.name}" supprimée`);
      setDelConfirm(null);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-1">Banques actives</h1>
        <p className="text-zinc-500">Toutes les banques créées sur la plateforme avec leur lien public.</p>
      </div>

      <div className="mb-2">
        <input
          className="border border-zinc-200 rounded-lg px-3 py-2 text-sm w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
          placeholder="Filtrer par nom, slug, admin…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Banque</th>
                <th className="text-left p-4">Admin</th>
                <th className="text-left p-4">URL publique</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-4">
                    <div className="font-semibold text-zinc-900">{b.name}</div>
                    <div className="text-xs text-zinc-400 font-mono">{b.slug}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-xs text-zinc-600">{b.admin_name}</div>
                    <div className="text-xs text-zinc-400">{b.admin_email}</div>
                  </td>
                  <td className="p-4">
                    <span className="font-mono text-xs text-blue-600">{b.public_url}</span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => window.open(b.public_url, "_blank")}
                        className="text-xs gap-1">
                        Voir <ExternalLink className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDelConfirm(b)}
                        className="text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                        <Trash2 className="w-3 h-3" /> Supprimer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={4} className="p-8 text-center text-zinc-400">Aucune banque{filter ? " correspondant au filtre" : ""}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!delConfirm} onOpenChange={(v) => !v && !delBusy && setDelConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la banque</DialogTitle>
          </DialogHeader>
          {delConfirm && (
            <div className="space-y-3 text-sm">
              <p>Êtes-vous sûr de vouloir supprimer définitivement la banque <strong>{delConfirm.name}</strong> ?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 space-y-1">
                <p className="font-semibold">⚠️ Cette action est irréversible et va supprimer :</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>La banque et toute sa configuration</li>
                  <li>Tous les utilisateurs finaux de la banque</li>
                  <li>Toutes les transactions, cartes, comptes d'épargne, crédits</li>
                  <li>L'historique des connexions et notifications</li>
                </ul>
                <p className="mt-2">L'abonnement de l'admin ne sera pas supprimé — il pourra recréer une nouvelle banque (frais de recréation appliqués).</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)} disabled={delBusy}>Annuler</Button>
            <Button onClick={handleDelete} disabled={delBusy} className="bg-red-600 hover:bg-red-700 text-white">
              {delBusy ? "Suppression…" : "Confirmer la suppression"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Platform Settings ────────────────────────────────────────────────────────
function PlatformSettings() {
  const [fee, setFee] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [announcement, setAnnouncement] = useState({ enabled: false, text: "" });
  const [annLoading, setAnnLoading] = useState(true);
  const [annSaving, setAnnSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/settings/bank-recreation-fee")
      .then((r) => setFee(String(r.data.value)))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/public/announcement")
      .then((r) => setAnnouncement({ enabled: r.data.enabled, text: r.data.text || "" }))
      .catch(() => {})
      .finally(() => setAnnLoading(false));
  }, []);

  const save = async () => {
    const v = parseInt(fee, 10);
    if (isNaN(v) || v < 0) { toast.error("Valeur invalide"); return; }
    setSaving(true);
    try {
      await api.put("/platform/settings/bank-recreation-fee", { value: v });
      toast.success("Paramètre enregistré");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  const saveAnnouncement = async () => {
    setAnnSaving(true);
    try {
      await api.put("/super-admin/announcement", announcement);
      toast.success("Bannière mise à jour");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setAnnSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-2">Paramètres de la plateforme</h1>
        <p className="text-zinc-500">Configure les tarifs globaux appliqués aux admins clients.</p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl p-6 max-w-xl">
        <h2 className="font-display text-lg font-bold mb-1">Frais de recréation de banque</h2>
        <p className="text-sm text-zinc-500 mb-4">
          Montant prélevé lorsqu'un admin recrée une banque sur un abonnement qui en avait déjà eu une.
          La suppression d'une banque est gratuite. Par défaut : 3 000 Coins.
        </p>
        {loading ? (
          <p className="text-sm text-zinc-400">Chargement…</p>
        ) : (
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label>Montant (Coins)</Label>
              <Input
                type="number"
                min="0"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                placeholder="3000"
              />
            </div>
            <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-700 text-white">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl p-6 max-w-xl">
        <h2 className="font-display text-lg font-bold mb-1">Bannière d'annonce</h2>
        <p className="text-sm text-zinc-500 mb-4">
          Affiche un message en haut de toutes les pages de <strong>neobk.pro</strong>. Les visiteurs peuvent fermer la bannière.
        </p>
        {annLoading ? (
          <p className="text-sm text-zinc-400">Chargement…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={announcement.enabled}
                onCheckedChange={(v) => setAnnouncement((prev) => ({ ...prev, enabled: v }))}
                id="ann-enabled"
              />
              <Label htmlFor="ann-enabled" className="cursor-pointer">
                {announcement.enabled ? "Bannière activée" : "Bannière désactivée"}
              </Label>
            </div>
            <div>
              <Label className="mb-1 block">Texte de l'annonce</Label>
              <Textarea
                value={announcement.text}
                onChange={(e) => setAnnouncement((prev) => ({ ...prev, text: e.target.value }))}
                placeholder="Ex: La plateforme est désormais disponible sur edbks.com. Les anciens liens ixt.finance / ixt.app redirigent automatiquement."
                rows={3}
              />
            </div>
            <Button onClick={saveAnnouncement} disabled={annSaving} className="bg-zinc-900 hover:bg-zinc-700 text-white">
              {annSaving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
